import { Module } from "../types";

/** API / practice filter question types (uppercase, matches backend enum). */
export type PracticeQuestionType = "MCQ" | "SJT" | "EMQ" | "ROLE_PLAY";

export type QuestionTypeCounts = Record<PracticeQuestionType, number>;

export const PRACTICE_QUESTION_TYPES: PracticeQuestionType[] = [
  "MCQ",
  "SJT",
  "EMQ",
  "ROLE_PLAY",
];

/** Display order for module table type-count pills. */
export const MODULE_TYPE_PILL_ORDER: PracticeQuestionType[] = [
  "MCQ",
  "EMQ",
  "SJT",
  "ROLE_PLAY",
];

export const emptyQuestionTypeCounts = (): QuestionTypeCounts => ({
  MCQ: 0,
  SJT: 0,
  EMQ: 0,
  ROLE_PLAY: 0,
});

/** Display label for type chips (Role Play instead of ROLE_PLAY). */
export function practiceQuestionTypeLabel(type: PracticeQuestionType): string {
  if (type === "ROLE_PLAY") return "Role Play";
  return type;
}

function formatModuleTypeNoun(
  type: PracticeQuestionType,
  count: number,
): string {
  const base = practiceQuestionTypeLabel(type);
  return count === 1 ? base : `${base}s`;
}

/** Inventory pill text, e.g. "100 MCQs" / "1 Role Play". */
export function formatModuleTypeCountLabel(
  type: PracticeQuestionType,
  count: number,
): string {
  return `${count} ${formatModuleTypeNoun(type, count)}`;
}

/** Non-zero type counts in module-pill display order. */
export function getNonZeroQuestionTypeEntries(
  counts?: Partial<QuestionTypeCounts> | null,
): Array<{ type: PracticeQuestionType; count: number }> {
  const normalized = normalizeCounts(counts);
  return MODULE_TYPE_PILL_ORDER.filter((type) => normalized[type] > 0).map(
    (type) => ({ type, count: normalized[type] }),
  );
}

function normalizeCounts(
  counts?: Partial<QuestionTypeCounts> | null
): QuestionTypeCounts {
  const base = emptyQuestionTypeCounts();
  if (!counts) return base;
  for (const type of PRACTICE_QUESTION_TYPES) {
    base[type] = Number(counts[type] ?? 0) || 0;
  }
  return base;
}

/** Sum inventory totals by type across modules. */
export function sumQuestionTypeTotals(modules: Module[]): QuestionTypeCounts {
  const totals = emptyQuestionTypeCounts();
  for (const mod of modules) {
    const byType = normalizeCounts(mod.numberOfQuestionsByType);
    for (const type of PRACTICE_QUESTION_TYPES) {
      totals[type] += byType[type];
    }
  }
  return totals;
}

/** Sum remaining (unattempted) by type across modules. */
export function sumQuestionTypeRemaining(modules: Module[]): QuestionTypeCounts {
  const remaining = emptyQuestionTypeCounts();
  for (const mod of modules) {
    const totals = normalizeCounts(mod.numberOfQuestionsByType);
    const completed = normalizeCounts(mod.numberOfQuestionsCompletedByType);
    for (const type of PRACTICE_QUESTION_TYPES) {
      remaining[type] += Math.max(0, totals[type] - completed[type]);
    }
  }
  return remaining;
}

/** Types present in the subject (total > 0). */
export function getTypesPresentInSubject(modules: Module[]): PracticeQuestionType[] {
  const totals = sumQuestionTypeTotals(modules);
  return PRACTICE_QUESTION_TYPES.filter((type) => totals[type] > 0);
}

/**
 * Keep selected types that still have inventory in the module selection.
 * Returns empty array to mean "All" when nothing remains selected.
 * Returns the same `selected` reference when unchanged (avoids effect loops).
 */
export function reconcileSelectedPracticeTypes(
  selected: PracticeQuestionType[],
  modulesInSelection: Module[]
): PracticeQuestionType[] {
  if (selected.length === 0) return selected;
  const totals = sumQuestionTypeTotals(modulesInSelection);
  const next = selected.filter((type) => totals[type] > 0);
  if (
    next.length === selected.length &&
    next.every((type, index) => type === selected[index])
  ) {
    return selected;
  }
  return next;
}

export function isPracticeTypeAvailableInSelection(
  type: PracticeQuestionType,
  modulesInSelection: Module[]
): boolean {
  return sumQuestionTypeTotals(modulesInSelection)[type] > 0;
}
